import React, { useState, useMemo, useEffect, useRef } from "react";
import classnames from "classnames";
import wx from "weixin-js-sdk";
import { useParams } from "react-router-dom";
import request from "./utils/request";

import callInvokeNative from "./utils/callInvokeNative";
import checkVersion from "./utils/checkVersion";

import UseOpenNativeWrap from "./components/useOpenNativeWrap";
import HotArticleList from "./components/HotArticleList";
import Comment from "./components/Comment";
import TaskSchedule from "./components/TaskSchedule";

import yugeIco from "./assets/images/footlogo.png";
import wxServiceIco from "./assets/images/footapp.png";
import noArticleIco from "./assets/images/no-article-pic.png";
import noArticleTxt from "./assets/images/no-article-txt.png";
import banner from "./assets/images/topbanner.png";

import "./assets/css/style.css";
import "./assets/css/animate.min.css";

function App() {
  const clientName = useMemo(() => {
    const ua = navigator.userAgent.toLowerCase();
    if (ua.indexOf("yugeyiliao") >= 0) {
      return "app";
    } else if (ua.indexOf("micromessenger") > -1) {
      return "wechat";
    } else if (ua.indexOf("yugemobilehospitalplatform_") > -1) {
      return "ehosApp";
    } else {
      return "other";
    }
  }, []);

  const { articleId } = useParams();

  const articleUrl = useRef(document.location.href.split("#")[0]);
  const reportReasonDomRef = useRef(null);
  const articleContentDomRef = useRef(null);

  const [fontSize, setFontSize] = useState("normal");

  const [articleInfo, setArticleInfo] = useState({});
  const [nextArticle, setNextArticle] = useState({});
  const [preArticle, setPreArticle] = useState({});

  const [likeCount, setLikeCount] = useState(0);
  const [isMyLike, setIsMyLike] = useState(false);
  const [commentCount, setCommentCount] = useState(0);

  const [reportArticleMaskShow, setReportArticleMaskShow] = useState(false);

  const [audioPlayStatus, setAudioPlayStatus] = useState(0); // 0 初始化或者播放完成  1 播放中   2  播放暂停

  const [audioUrlList, setAudioUrlList] = useState([]);
  const [audioIndex, setAudioIndex] = useState(0);
  const [canPlayAudio, setCanPlayAudio] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    // 获取文章详情
    const getArticleDetailRes = async () => {
      const data = await request.post(
        `/unauthorize/articleDetail/id/${articleId}`
      );
      if (data.resultCode === "0") {
        const {
          articleInfo = undefined,
          nearArticle = [],
          hospitalMembAccountInfo = {},
          userImageURL,
        } = data.result;

        if (!articleInfo || !articleInfo.content) {
          setArticleInfo(articleInfo);
          return;
        }

        document.title = articleInfo.title;

        // 微信端 配置分享信息
        if (clientName === "wechat") {
          const shareTitle = `渔歌医疗|${articleInfo.title}`;
          const desc = articleInfo.articleDesc;
          const url = articleUrl.current;
          const imageUrl = articleInfo.articleImg;
          wx.ready(function () {
            wx.updateAppMessageShareData({
              title: shareTitle, // 分享标题
              desc: desc, // 分享描述
              link: url, // 分享链接
              imgUrl: imageUrl, // 分享图标
            });
            wx.updateTimelineShareData({
              title: shareTitle, // 分享标题
              link: url,
              imgUrl: imageUrl, // 分享图标
              desc: desc, // 分享描述
            });
          });
        }
        const { accountId = null } = hospitalMembAccountInfo;
        setArticleInfo({ ...articleInfo, accountId, userImageURL });
        setNextArticle(nearArticle[0] || {});
        setPreArticle(nearArticle[1] || {});

        if (checkVersion(641, 603)) {
          const autoReadData = await request.post("/newsReadConf/auToRead", {
            tagId: "9999",
          });

          if (autoReadData.resultCode === "0") {
            if (autoReadData.result.data === true) {
              // 触发自动朗读

              const readContentRes = await request.post(
                `/appInformationRelated/2/${articleId}`
              );
              if (readContentRes.resultCode === "0") {
                const { content, title } = readContentRes.result;
                // 触发自动朗读
                callInvokeNative("speakText", { text: `${title}。${content}` });
              }
            }
          }
        }
      } else {
        setArticleInfo(undefined);
      }
    };

    // 是否是本人已点赞 (有权限)
    const getIsLikeRes = async () => {
      const data = await request.post("/unauthorize/commentH5/isLike", {
        articleId: articleId,
        articleType: "article",
      });

      if (data.resultCode === 0) {
        const { like, articleMyLike = {}, commentCount: cCount } = data.data;

        setIsMyLike(like === 1);

        callInvokeNative("showCommentView", {
          isShow: true,
          commentCount: cCount,
          isCollect: !!articleMyLike.id,
          myLikeId: articleMyLike.id ? articleMyLike.id : null,
        }); // app 内唤起评论
      }
    };

    // 获取点赞数及评论数 （无权限校验）
    const getCommentAndLikeCountRes = async () => {
      const param = {
        articleId: articleId,
        articleType: "article",
      };
      const data = await request.post(
        "/unauthorize/commentH5/getIsMyLike",
        param
      );

      if (data.resultCode === "0") {
        const { commentCount: cCount, likeCount: lCount } = data.result;
        setLikeCount(lCount);
        setCommentCount(cCount);
      }
    };

    getArticleDetailRes(); // 文章详情

    getCommentAndLikeCountRes(); // 点赞数及评论数

    if (clientName === "app") {
      getIsLikeRes(); // 本人是否点赞
    }

    // 初始化微信config
    if (clientName === "wechat") {
      const getWxConfigRes = async () => {
        const url = articleUrl.current;
        const data = await request.post("/commWeixin/getSignature", { url });
        if (data.resultCode === "0") {
          const { timestamp, nonceStr, signature } = data.result;

          wx.config({
            debug: false,
            appId: "wxb25b6a530fffb318", // 必填，公众号的唯一标识
            timestamp, // 必填，生成签名的时间戳
            nonceStr, // 必填，生成签名的随机串
            signature, // 必填，签名
            jsApiList: [
              "updateAppMessageShareData", // 分享朋友圈
              "updateTimelineShareData", // 分享好友
            ], // 必填，需要使用的JS接口列表
            openTagList: ["wx-open-launch-app"],
          });
        }
      };

      getWxConfigRes();
    }

    const getArticleReadUrl = async () => {
      const data = await request.post(
        `/unauthorize/articleDetail/audioUrlList/${articleId}`
      );

      if (data.resultCode === "0") {
        const { audioUrlList } = data.result;
        setAudioIndex(0);
        setAudioUrlList(audioUrlList);
      }
    };

    // 不在app内获取朗读url
    if (clientName !== "app") {
      getArticleReadUrl();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const handle = (e) => {
      if (!articleInfo?.content) {
        return;
      }
      const allImages = articleContentDomRef.current.querySelectorAll("img");
      const imgUrlList = Array.from(allImages).map((item) =>
        item.getAttribute("src")
      );

      const tag = e.target.tagName;

      // 如果父标签是a 则默认图片不打开
      const parentIsLink =
        e.target.parentNode.tagName.toLowerCase() === "a" ||
        e.target.parentNode.parentNode.tagName.toLowerCase() === "a";

      if (tag.toLowerCase() === "img" && !parentIsLink) {
        const current = e.target.getAttribute("src");

        if (clientName === "wechat") {
          wx.ready(() => {
            wx.previewImage({
              current,
              urls: imgUrlList,
            });
          });
        }

        if (clientName === "app") {
          callInvokeNative("startImageViewer", {
            url: current,
            urlList: imgUrlList,
          });
        }
      }
    };

    const target = document.querySelector("#content");

    target?.addEventListener("click", handle);

    return () => {
      target?.removeEventListener("click", handle);
    };
  }, [articleInfo?.content, clientName]);

  const changeFontSize = (e, size) => {
    e.stopPropagation();
    e.preventDefault();

    setFontSize(size);
  };

  const onPlayAudio = async (e) => {
    e.stopPropagation();

    let nextStatus = 0;
    if (audioPlayStatus === 0) {
      nextStatus = 1;
    } else if (audioPlayStatus === 1) {
      nextStatus = 2;
    } else {
      nextStatus = 1;
    }
    setAudioPlayStatus(nextStatus);
    for (let i = 0; i < audioUrlList.length; i++) {
      if (audioPlayStatus !== 1 && audioIndex === i) {
        document.querySelector(`#audio${i}`).play();
      } else {
        document.querySelector(`#audio${i}`).pause();
      }
    }
  };

  const onLikeOrCancelLike = async (e) => {
    e.stopPropagation();

    const param = {
      articleId: articleId,
      articleType: "article",
    };

    const data = await request.post(
      "/unauthorize/commentH5/likeOrCancelLike",
      param
    );

    if (data.resultCode === 0) {
      const { likeCount: lCount } = data.data;

      setLikeCount(lCount);
      setIsMyLike((pre) => !pre);
    }
  };

  const onReportArticle = (e) => {
    e.stopPropagation();
    if (reportReasonDomRef.current) {
      reportReasonDomRef.current.value = "";
    }
    setReportArticleMaskShow(true);
  };

  const onSureReport = async (e) => {
    e.stopPropagation();
    e.preventDefault();

    const description = reportReasonDomRef.current.value;

    if (!description) {
      window.alert("请输入原因");
      return;
    }

    const param = {
      id: articleId,
      type: "3",
      description,
    };

    const data = await request.post("/unauthorize/commentH5/articleTip", param);

    if (data.resultCode === "0") {
      window.alert("感谢反馈！");
    }

    setReportArticleMaskShow(false);
  };

  const openProfile = (e, accountId) => {
    e.stopPropagation();

    if (clientName === "app" && accountId) {
      const param = {
        accountId: `${accountId}`,
        accountType: "2",
      };
      callInvokeNative("openProfile", param);
    } else if (accountId) {
      window.location.href = `https://m.yuge.com/doctorInfo/profile.html?accountId=${accountId}`;
    }
  };

  if (typeof articleInfo === "undefined") {
    return (
      <div>
        <div className="tc mtt45">
          <img src={noArticleIco} width="73" alt="" />
        </div>
        <div className="tc mt40">
          <img src={noArticleTxt} width="107" alt="" />
        </div>
      </div>
    );
  }

  return (
    <div>
      {clientName !== "app" && clientName !== "ehosApp" && (
        <div
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            animation: "flip1 0.5s ease-in",
            zIndex: 1,
          }}
        >
          <UseOpenNativeWrap
            clientName={clientName}
            articleUrl={articleUrl.current}
          >
            <img src={banner} alt="" width="100%" />
          </UseOpenNativeWrap>
        </div>
      )}

      <div className="wrap">
        <div>
          <section
            className={classnames("warparea", {
              pt50: clientName !== "app" && clientName !== "ehosApp",
            })}
          >
            <div className="ml25 mr25 fontsize-bar">
              <div className="fontsize-bar-left">
                <i className="mr5 fontsize-ico"></i>
                <span>字号</span>
              </div>

              <ul className="fontsize-list">
                <li className={classnames({ active: fontSize === "small" })}>
                  <a href="##" onClick={(e) => changeFontSize(e, "small")}>
                    小
                  </a>
                </li>
                <li className={classnames({ active: fontSize === "normal" })}>
                  <a href="##" onClick={(e) => changeFontSize(e, "normal")}>
                    标准
                  </a>
                </li>
                <li className={classnames({ active: fontSize === "big" })}>
                  <a href="##" onClick={(e) => changeFontSize(e, "big")}>
                    大
                  </a>
                </li>
              </ul>

              {clientName !== "app" && clientName !== "ehosApp" && (
                <>
                  {audioUrlList.map((item, index) => (
                    <audio
                      key={item}
                      src={item}
                      width="1"
                      height="1"
                      preload="metadata"
                      onLoadedMetadata={() => {
                        if (index === 0) {
                          setCanPlayAudio(true);
                        }
                      }}
                      onEnded={() => {
                        if (audioIndex === audioUrlList.length - 1) {
                          setAudioIndex(0);
                          setAudioPlayStatus(0);
                          document.querySelector(`#audio${audioIndex}`).pause();
                        } else {
                          setAudioIndex(audioIndex + 1);
                          document.querySelector(`#audio${audioIndex}`).pause();
                          document
                            .querySelector(`#audio${audioIndex + 1}`)
                            .play();
                        }
                      }}
                      id={`audio${index}`}
                    />
                  ))}

                  {canPlayAudio && (
                    <div className="fontsize-bar-right">
                      <p
                        className="weixinAudio read-aloud"
                        style={{
                          padding: 0,
                          paddingRight: 5,
                        }}
                      >
                        <span onClick={onPlayAudio}>
                          <span
                            className={classnames("audio_play_area", {
                              playing: audioPlayStatus === 1,
                            })}
                            style={{
                              float: "none",
                              margin: 0,
                              fontSize: 14,
                              width: "auto",
                              height: 30,
                              background: "none",
                            }}
                          >
                            <span
                              id="playText"
                              className={classnames({
                                "de-ld": audioPlayStatus === 0,
                              })}
                            >
                              <i
                                className={classnames({
                                  voice: audioPlayStatus === 0,
                                  play: audioPlayStatus === 1,
                                  suspend: audioPlayStatus === 2,
                                })}
                              ></i>
                              {audioPlayStatus === 0 && "朗读全文"}
                              {audioPlayStatus === 1 && "正在朗读"}
                              {audioPlayStatus === 2 && "暂停朗读"}
                            </span>
                          </span>
                        </span>
                      </p>
                    </div>
                  )}
                </>
              )}
            </div>
            <p
              className={classnames("ml25 mr25 mt20 mb10 speech_title", {
                fS22: fontSize === "normal",
                fS18: fontSize === "small",
                fS26: fontSize === "big",
              })}
            >
              {articleInfo.title}
            </p>
            <div className="clear pr article-author ml25 mr25">
              <span
                className="cgrayq fS14 author-box"
                onClick={(e) => openProfile(e, articleInfo.accountId)}
              >
                <img
                  src={
                    articleInfo.userImageURL ||
                    "https://m.yuge.com/authorize/dist/resources/images/docDefault.png"
                  }
                  alt=""
                  width="30"
                />
                {articleInfo.doctor?.name}
              </span>

              <span className="cgrayq fS14">{articleInfo.createTime}</span>
            </div>
            <div
              id="content"
              className={classnames("clear ml25 mr25 mt10 newsbody-size", {
                fS18: fontSize === "normal",
                fS14: fontSize === "small",
                fS22: fontSize === "big",
              })}
              dangerouslySetInnerHTML={{ __html: articleInfo.content }}
              ref={articleContentDomRef}
            />

            <div className="fS16 tc mt20">免责声明</div>
            <div className="fS12 cgrayq ml20 mr20 mt5 lh18">
              本站医生文章版权仅归原作者所有，若作者有版权声明或文章从其它网站转载而附带有原所有站的版权声明者，其版权归属以附带声明为准，转载请联系原作者并获许可。文章观点仅代表作者本人，不代表渔歌医疗立场。
            </div>
            <div className="clear mt30 mb20">
              <span
                className="fl fS14 cgrayq ml25"
                style={{
                  width: "57%",
                }}
              >
                原创：
                {articleInfo.doctor?.name}
              </span>
              <span className="fr fS14 cgrayq mr25">
                阅读（{articleInfo.pageView}）
              </span>
            </div>

            {clientName !== "ehosApp" && (
              <div className="tc pr">
                <UseOpenNativeWrap
                  clientName={clientName}
                  articleUrl={articleUrl.current}
                  onClick={onLikeOrCancelLike}
                >
                  <div
                    className={classnames({
                      dianzhan: !isMyLike,
                      "dianzhan-active": isMyLike,
                    })}
                  >
                    <i className="comment-bg zan-ico mr5"></i>
                    {likeCount}
                  </div>
                </UseOpenNativeWrap>

                <UseOpenNativeWrap
                  clientName={clientName}
                  articleUrl={articleUrl.current}
                  onClick={onReportArticle}
                >
                  <div className="tc pa-r mr25 lh20 fS12">
                    <i className="comment-bg jubao-ico"></i>
                    <div>举报</div>
                  </div>
                </UseOpenNativeWrap>
              </div>
            )}
          </section>

          <div className="warparea ">
            {preArticle.ID && (
              <div className="fS14 ml20 mr20 lh26">
                <a href={`#/${preArticle.ID}`} className="cgray9">
                  上一篇：{preArticle.title}
                </a>
              </div>
            )}

            {nextArticle.ID && (
              <div className="fS14 ml20 mr20 lh26">
                <a href={`#/${nextArticle.ID}`} className="cgray9">
                  下一篇：{nextArticle.title}
                </a>
              </div>
            )}
          </div>

          <HotArticleList articleId={articleId} />

          {clientName !== "ehosApp" && (
            <>
              <Comment
                articleUrl={articleUrl.current}
                clientName={clientName}
                articleId={articleId}
                commentCount={commentCount}
                setCommentCount={setCommentCount}
              />

              <div className="bg_w">
                <div className="info-2c-news">
                  <div className="fl ww50 tc">
                    <img src={yugeIco} width="65%" alt="" />
                    <p className="mt5">渔&nbsp;歌&nbsp;医&nbsp;疗</p>
                  </div>
                  <div className="fl ww50 tc ">
                    <img src={wxServiceIco} width="65%" alt="" />
                    <p className="mt5">长按识别二维码关注</p>
                  </div>
                </div>
                <div className="foot-slogan-m">
                  渔歌医疗致力于为医联体/医共体、医生集团、区域性分级诊疗提供连续的服务与解决方案
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* 举报 投诉原因 */}
      {reportArticleMaskShow && (
        <div className="pop-bg">
          <div className="ww70 marginauto mtt30">
            <div className="modal-content pr">
              <span
                className="ico-bg closesmall-ico pa-r mt17 mr10"
                onClick={() => setReportArticleMaskShow(false)}
              />
              <div className="fS16 mt10 tc fB">举报与投诉</div>
              <div className="tc mt10">
                <textarea
                  placeholder="请输入举报原因"
                  className="ww90 fS14 tl"
                  ref={reportReasonDomRef}
                />
              </div>
              <div className="tc mb20 mt20">
                <a className="btn-sm" href="##" onClick={onSureReport}>
                  提交
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* 任务进度 */}
      <TaskSchedule clientName={clientName} articleId={articleId} />
    </div>
  );
}

export default App;
